import { useEffect, useRef, useState } from "react";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";
import { Tooltip } from "primereact/tooltip";
import { ValidationMessage } from "sj-ui-toolkit/src/components";
import { useStrictKnowledgeAssessment } from "sj-ui-toolkit/src/hooks";
import {
  notificationOptions,
  reminderTextMap,
  type NotificationOption,
} from "@/constants/knowledgeAssessments";
import {
  findDaysBetween,
  normalizeToMidnight,
  setToEndOfDay,
} from "@/helpers/knowledgeAssessments";

interface AssessmentDates {
  availableAt?: Date | string;
  dueAfter?: { days: number };
  dynamic?: boolean;
}

interface AssessmentNotify {
  notifyWhenAvailable: boolean;
  sendReminders: string;
}

interface AssessmentItem {
  dates: AssessmentDates;
  notify: AssessmentNotify;
  strict?: Record<string, unknown>;
}

interface AssessmentFormSettingsProps {
  handleChange: (values: AssessmentItem[]) => void;
  values: AssessmentItem[];
  startDate: Date | string | undefined;
  setStartDate: (date: Date | string) => void;
  endDate: Date | string | undefined;
  setEndDate: (date: Date | string) => void;
  showValidationMessages?: boolean;
  validationErrors: { dates: { hasError: boolean; message: string } };
}

export default function AssessmentFormSettings({
  handleChange,
  values,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  showValidationMessages,
  validationErrors,
}: AssessmentFormSettingsProps) {
  const item = values[0] ?? {
    dates: {},
    notify: { notifyWhenAvailable: true, sendReminders: "None" },
  };
  const notify = item.notify;
  const sendRemindersFromProps = values[0]?.notify?.sendReminders;
  const notifyWhenAvailableFromProps = values[0]?.notify?.notifyWhenAvailable;
  const isStrictEnabled = useStrictKnowledgeAssessment();

  const [strictAssignment, setStrictAssignment] = useState(!!item.strict);
  const [notifyWhenAvailable, setNotifyWhenAvailable] = useState(notify.notifyWhenAvailable);
  const [sendReminders, setSendReminders] = useState(notify.sendReminders);
  const [daysBetween, setDaysBetween] = useState(0);

  // For dynamic date mode
  const [dynamicDateValue, setDynamicDateValue] = useState(daysBetween.toString());
  const dynamicInputRef = useRef<HTMLInputElement>(null);

  // Sync notification state from props when assessment data arrives asynchronously
  // (edit mode). SVC returns StartCase values ("None", "Weekly", …) which match
  // the StartCase options in notificationOptions.
  useEffect(() => {
    if (sendRemindersFromProps === undefined && notifyWhenAvailableFromProps === undefined) return;
    setSendReminders(sendRemindersFromProps ?? "None");
    setNotifyWhenAvailable(notifyWhenAvailableFromProps ?? true);
  }, [sendRemindersFromProps, notifyWhenAvailableFromProps]);

  useEffect(() => {
    const days = findDaysBetween(startDate, endDate);
    setDaysBetween(days);
  }, [startDate, endDate]);

  useEffect(() => {
    if (item.dates?.dynamic) {
      setDynamicDateValue(daysBetween.toString());
    }
  }, [daysBetween, item.dates?.dynamic]);

  useEffect(() => {
    if (item.dates?.dynamic && dynamicInputRef.current) {
      dynamicInputRef.current.focus();
    }
  }, [item.dates?.dynamic]);

  const updateValues = (patch: Partial<AssessmentItem>) => {
    const updated = [{ ...item, ...patch }];
    handleChange(updated);
  };

  const handleStartDateChange = (e: { value?: Date | null }) => {
    const newStartDate = normalizeToMidnight(e.value);
    if (!newStartDate) return;
    setStartDate(newStartDate);
    if (endDate && new Date(newStartDate) > new Date(endDate)) {
      setEndDate("");
    }
    updateValues({
      dates: {
        ...item.dates,
        availableAt: newStartDate,
        dueAfter: { ...item.dates?.dueAfter, days: findDaysBetween(newStartDate, endDate) },
      },
    });
  };

  const handleEndDateChange = (e: { value?: Date | null }) => {
    const newEndDate = setToEndOfDay(e.value);
    if (!newEndDate) return;
    setEndDate(newEndDate);
    updateValues({
      dates: {
        ...item.dates,
        dueAfter: { ...item.dates?.dueAfter, days: findDaysBetween(startDate, newEndDate) },
      },
    });
  };

  const toggleDynamicMode = (isDynamic: boolean) => {
    updateValues({ dates: { ...item.dates, dynamic: isDynamic } });
  };

  const handleDynamicDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!startDate) return;
    const raw = e.target.value;
    setDynamicDateValue(raw === "" ? "0" : raw);
    const totalDays = parseInt(raw, 10) || 0;
    const inclusiveEndOffset = totalDays > 0 ? totalDays - 1 : 0;
    const newEndDate = new Date(startDate as string | Date);
    newEndDate.setDate(newEndDate.getDate() + inclusiveEndOffset);
    handleEndDateChange({ value: newEndDate });
  };

  const onNotifyWhenAvailableChange = () => {
    const newValue = !notifyWhenAvailable;
    setNotifyWhenAvailable(newValue);
    updateValues({ notify: { ...notify, notifyWhenAvailable: newValue } });
  };

  const onSendRemindersChange = (value: string) => {
    setSendReminders(value);
    updateValues({ notify: { ...notify, sendReminders: value } });
  };

  const onStrictAssignmentChange = (e: { checked?: boolean }) => {
    setStrictAssignment(e.checked ?? false);
    if (e.checked) {
      updateValues({ strict: {} });
    } else {
      const { strict: _removed, ...rest } = item;
      handleChange([rest as AssessmentItem]);
    }
  };

  const isDynamic = !!item.dates.dynamic;

  return (
    <div className="grid lg:grid-cols-2 gap-3">
      {/* Left column: dates */}
      <div>
        <div className="flex flex-col gap-2 mb-4">
          <label htmlFor="start-date">Start Date:</label>
          <Calendar
            id="start-date"
            name="start-date"
            className="max-w-64"
            minDate={new Date()}
            value={startDate as Date | undefined}
            placeholder="Select start date"
            onChange={handleStartDateChange}
            showIcon
          />
        </div>

        <fieldset
          disabled={!startDate}
          className={`${!startDate && "opacity-50"} flex flex-col gap-4`}
        >
          <legend>Time Frame (required):</legend>
          <div className="flex flex-col gap-3 m-4">
            {/* Fixed date mode */}
            <div className="flex items-center gap-2">
              <RadioButton
                inputId="fixed-date"
                name="dateMode"
                value="fixed"
                checked={!isDynamic}
                onChange={() => toggleDynamicMode(false)}
              />
              <label htmlFor="fixed-date">Fixed Date</label>
              <Tooltip target="#fixed-date-tooltip" mouseTrack mouseTrackLeft={10}>
                Users added after the start date will still have the same end date
              </Tooltip>
              <i id="fixed-date-tooltip" className="pi pi-question-circle cursor-pointer" />
            </div>
            <fieldset disabled={isDynamic}>
              <legend className="sr-only">Fixed end date</legend>
              <div className="flex flex-col gap-1 m-4">
                <label htmlFor="end-date">End Date</label>
                <Calendar
                  id="end-date"
                  name="end-date"
                  className="max-w-64"
                  value={isDynamic ? undefined : (endDate as Date | undefined)}
                  minDate={startDate ? new Date(startDate) : new Date()}
                  placeholder="Select end date"
                  onChange={handleEndDateChange}
                  showIcon
                />
                {!isDynamic && <span className="body-sm">{daysBetween} days</span>}
              </div>
            </fieldset>

            {/* Dynamic date mode */}
            <div className="flex items-center gap-2">
              <RadioButton
                inputId="dynamic-date"
                name="dateMode"
                value="dynamic"
                checked={isDynamic}
                onChange={() => toggleDynamicMode(true)}
              />
              <label htmlFor="dynamic-date-input">Dynamic Date</label>
              <Tooltip target="#dynamic-date-tooltip" mouseTrack mouseTrackLeft={10}>
                Users will have the same amount of time to complete the assignment
              </Tooltip>
              <i id="dynamic-date-tooltip" className="pi pi-question-circle cursor-pointer" />
            </div>
            <fieldset disabled={!isDynamic}>
              <legend className="sr-only">Dynamic date duration</legend>
              <div className="m-4">
                <InputText
                  id="dynamic-date-input"
                  name="dynamic-date-input"
                  disabled={!isDynamic}
                  ref={dynamicInputRef}
                  value={isDynamic ? dynamicDateValue : ""}
                  onChange={handleDynamicDateChange}
                  keyfilter="int"
                  className="max-w-64"
                />
                <p className="body-sm mt-1">Assignment Duration (days)</p>
              </div>
            </fieldset>
          </div>
        </fieldset>
      </div>

      {/* Right column: notifications */}
      <div>
        <div className="mb-4">
          <p>Notify Learners:</p>
          <div className="mt-2 flex items-center gap-2">
            <Checkbox
              inputId="notify-when-available"
              checked={notifyWhenAvailable}
              onChange={onNotifyWhenAvailableChange}
            />
            <label htmlFor="notify-when-available">
              Notify learners when the assessment becomes available
            </label>
          </div>
        </div>

        <div className="mb-4">
          <p>Send Reminders:</p>
          {notificationOptions.map(option => (
            <div key={option} className="mt-2 flex items-center gap-2">
              <RadioButton
                inputId={`reminder-${option}`}
                name="sendReminders"
                value={option}
                onChange={() => onSendRemindersChange(option)}
                checked={sendReminders === option}
              />
              <label htmlFor={`reminder-${option}`}>{option}</label>
              <Tooltip target={`#notification-tooltip-${option}`}>
                {reminderTextMap[option as NotificationOption]}
              </Tooltip>
              <i
                id={`notification-tooltip-${option}`}
                className="pi pi-question-circle cursor-pointer"
              />
            </div>
          ))}
        </div>

        {isStrictEnabled && (
          <div>
            <p>Strict Assignment:</p>
            <div className="mt-2 flex items-center gap-2">
              <Checkbox
                inputId="strict-assignment"
                onChange={onStrictAssignmentChange}
                checked={strictAssignment}
              />
              <label htmlFor="strict-assignment">
                Lock all other content to users until this assignment is completed.
              </label>
            </div>
          </div>
        )}
      </div>

      {showValidationMessages && validationErrors.dates.hasError && (
        <div className="mt-2 col-span-2">
          <ValidationMessage severity="error" text={validationErrors.dates.message} />
        </div>
      )}
    </div>
  );
}
